import { observable, action } from "mobx";
import * as PRJAPI from 'cd-eft/services/eftPrjAdminApi';
import * as SETAPI from 'cd-eft/services/eftSetApi';
import MessageCenter from 'cd-eft/components/common/MessageCenter/MessageCenterVM';
import { toDecimalPrecision } from 'cd-eft/utils/mathUtilities';
import { SoilParametersDTO } from "cd-eft/_codegen_/openapi-generator/eft-set"
import stores from '../../../../models/Stores'
import { IWscVmProps } from "../WaterwaySizingCalculatorVM";

type clearableInput = number | "" | "RESET";
export interface IErodibilityForm {
    erodibility: string;
    allowableStress: number;
    soilGrainRoughness: number;
}

export interface ISoilParameterForm {
    soilType: string;
    plasticityIndex: clearableInput;
    allowableStress: number | "";
    voidRatio: clearableInput;
    d75: clearableInput;
    soilGrainRoughness: number | "";
}

export interface ISoilStressForm {
    allowableStress: clearableInput;
    soilGrainRoughness: clearableInput;
}

export interface ISoilFormSelect {
    type: 'erodibility' | 'soil_parameter' | 'soil_stress';
}

interface ISoilParameterFormClassName {
    plasticityIndex: '' | ' usa-input--error',
    voidRatio: '' | ' usa-input--error',
    d75: '' | ' usa-input--error',
    soilType: '' | ' usa-input--error',
    allowableStress: 'calculated-result-box' | 'usa-input--error'
}

interface ISoilStressFormClassName {
    allowableStress: '' | ' usa-input--error',
    soilGrainRoughness: '' | ' usa-input--error',
}

const waterwayDesignStore = stores.AppStore.waterwayDesignStore;

export class SoilStrengthFormVM {
    @observable erodibilityMenu: any[] = [];
    @observable soilTypeOptions: any[] = [];
    @observable soilTypeMenu: any[] = [];
    @observable erodibilityOptions: any[] = [];
    @observable muteSoilInfo: boolean = false;
    mode: 'design' | 'standalone' | 'adminGlobalPreferences' = 'standalone';
    prefJson: any = {};

    @observable soilFormSelect: ISoilFormSelect = {
        type: 'erodibility'
    }

    @observable erodibilityForm: IErodibilityForm = {
        erodibility: '',
        allowableStress: 0,
        soilGrainRoughness: 0,
    }

    @observable soilParameterForm: ISoilParameterForm = {
        soilType: "",
        plasticityIndex: "",
        allowableStress: "",
        voidRatio: "",
        d75: "",
        soilGrainRoughness: "",

    }



    @observable soilStressForm: ISoilStressForm = {
        allowableStress: "",
        soilGrainRoughness: 0.0156
    }

    @observable erodabilityClassName: '' | ' usa-input--error' = "";

    @observable soilParameterFormClassName: ISoilParameterFormClassName = {
        plasticityIndex: "",
        voidRatio: "",
        d75: "",
        soilType: "",
        allowableStress: "calculated-result-box",
    }

    @observable soilStressFormClassName = {
        allowableStress: "",
        soilGrainRoughness: ""
    }

    @observable soilTypeInformatinalErrorCodes = {
        // '': 'Void Ratio between 0.35 and 1.50, Plasticity index between 0.01 and 60.00D75 between 0.01 and 1.00, D75 between 0.01 and 1.00',
        SC: 'WDT_47',
        GP: 'WDT_57',
        GC: 'WDT_48',
        CH: 'WDT_49',
        CL: 'WDT_50',
        OH: 'WDT_60',
        SW: 'WDT_58',
        SP: 'WDT_59',
        GM: 'WDT_52',
        SM: 'WDT_53',
        MH: 'WDT_54',
        ML: 'WDT_55',
        OL: 'WDT_61',
        GW: 'WDT_56',
    }

    // Form Validation model, false means invalid, true means valid
    @observable formValidation = {

        erodibilityForm: {
            erodibility: false,
        },

        soilParameterForm: {
            soilType: false,
            plasticityIndex: false,
            voidRatio: false,
            d75: false,
            // allowableStress and soilGrainRoughness are calculated-- set true for now 
            allowableStress: false,
            soilGrainRoughness: false,

            // need to know if plasticityIndex/void ratio/d75 has been changed from intial values 
            // because user might change the soil type after they fill out all the value and we want to 
            // reevaluate the form.
            plasticityIndexInitialValueChange: false,
            voidRatioInitialValueChange: false,
            d75InitialValueChange: false,
        },

        soilStressForm: {
            allowableStress: false,
            // soilGrainRoughness is default---set for true for now 
            soilGrainRoughness: true,
            allowableStressInitialValueChange: false,
            soilGrainRoughnessInitialValueChange: false
        }
    };

    @observable formaValidationResult = {
        validationResultErodibilityForm: false,
        validationResultSoilParameterForm: false,
        validationResultSoilStressForm: false,
    }
    constructor(props: IWscVmProps) {
        this.mode = props.mode;
        this.prefJson = props.prefJson;
    }

    get erodibilitySelect(): boolean {
        return this.soilFormSelect.type === 'erodibility';
    }
    get soilParameterSelect(): boolean {
        return this.soilFormSelect.type === 'soil_parameter';
    }
    get soilStressSelect(): boolean {
        return this.soilFormSelect.type === 'soil_stress';
    }

    get erodibilityFormValidation(): boolean {
        if (this.mode === 'adminGlobalPreferences' && this.erodibilityForm.erodibility) { return true }
        this.formaValidationResult.validationResultErodibilityForm = (this.formValidation.erodibilityForm.erodibility === true);
        if (!this.formValidation.erodibilityForm.erodibility) {
            MessageCenter.addNotificationFromCode("Common_09", undefined, "Erodibility");
            this.erodabilityClassName = ' usa-input--error';
        }
        return this.formaValidationResult.validationResultErodibilityForm;
    }

    get soilParameterFormValidation(): boolean {
        this.formaValidationResult.validationResultSoilParameterForm =
            (this.formValidation.soilParameterForm.soilType
                && this.formValidation.soilParameterForm.d75
                && this.formValidation.soilParameterForm.plasticityIndex
                && this.formValidation.soilParameterForm.soilGrainRoughness
                && this.formValidation.soilParameterForm.voidRatio
                && this.formValidation.soilParameterForm.allowableStress);

        if (!this.formValidation.soilParameterForm.soilType) {
            this.soilParameterFormClassName.soilType = ' usa-input--error';
        }
        if (!this.formValidation.soilParameterForm.plasticityIndex) {
            this.soilParameterFormClassName.plasticityIndex = ' usa-input--error';
        }
        if (!this.formValidation.soilParameterForm.voidRatio) {
            this.soilParameterFormClassName.voidRatio = ' usa-input--error';
        }
        if (!this.formValidation.soilParameterForm.d75) {
            this.soilParameterFormClassName.d75 = ' usa-input--error';
        }
        if (!this.formValidation.soilParameterForm.allowableStress) {
            this.soilParameterFormClassName.allowableStress = 'usa-input--error'
        }
        return this.formaValidationResult.validationResultSoilParameterForm;
    }

    get soilStressFormValidation(): boolean {
        this.formaValidationResult.validationResultSoilStressForm = this.formValidation.soilStressForm.allowableStress === this.formValidation.soilStressForm.soilGrainRoughness === true;
        if (!this.formValidation.soilStressForm.allowableStress) {
            this.soilStressFormClassName.allowableStress = ' usa-input--error';
        }
        if (!this.formValidation.soilStressForm.soilGrainRoughness) {
            this.soilStressFormClassName.soilGrainRoughness = ' usa-input--error';
        }
        return this.formaValidationResult.validationResultSoilStressForm
    }

    get soilStrengthSummaryValidation(): boolean {
        switch (this.soilFormSelect.type) {
            case 'erodibility':
                return this.erodibilityFormValidation;
            case 'soil_parameter':
                return this.soilParameterFormValidation;
            case 'soil_stress':
                return this.soilStressFormValidation;
            default:
                return false;
        }
    }

    @action.bound async handleErodibilityInputChange(event) {
        const { name, value } = event.target;
        // set value
        if (name === 'erodibility_dropdown') {
            this.erodibilityForm.erodibility = value;
            if (value !== '' && this.mode !== 'adminGlobalPreferences') {
                this.erodibilityForm.allowableStress = (this.erodibilityMenu as any[]).find((obj) => obj.label === value).allowableStress;
                this.erodibilityForm.soilGrainRoughness = (this.erodibilityMenu as any[]).find((obj) => obj.label === value).roughness;
            }
        }
        // set vadility 
        if (name === 'erodibility_dropdown' && value === '') {
            this.formValidation.erodibilityForm.erodibility = false;
            this.erodabilityClassName = ' usa-input--error';
        }
        else if (name === 'erodibility_dropdown' && value !== '') {
            this.formValidation.erodibilityForm.erodibility = true;
            this.erodabilityClassName = ''
        }
    }

    @action.bound async handleOnBlurErodibility(event) {
        const { name, value } = event.target;
        if (name === 'erodibility_dropdown' && value === '') {
            this.formValidation.erodibilityForm.erodibility = false;
            this.erodabilityClassName = ' usa-input--error';
            await MessageCenter.addNotificationFromCode("Common_09", undefined, "Erodibility", undefined, undefined);
        }
    }

    @action.bound handleOnKeyDown(event) {
        (["e", "E", "+", "-"].includes(event.key)) && event.preventDefault()
    }

    @action.bound toggleMuteAllSoilNotifs() {
        this.muteSoilInfo = !this.muteSoilInfo;
    }

    @action.bound handleSoilParameterInputChange(event) {
        const { name, value } = event.target;
        this.soilParameterFormClassName.allowableStress = "calculated-result-box";

        if (name === 'soil_type') {
            // set value 
            this.soilParameterForm.soilType = value;
            // informational notification pop when soil type is selected 
            if (value !== "" && value !== undefined && !this.muteSoilInfo) {
                MessageCenter.addNotificationFromCode(this.soilTypeInformatinalErrorCodes[value], [
                    {
                        callback: this.toggleMuteAllSoilNotifs,
                        text: 'Don\'t show this again',
                        type: 1,
                        value: this.muteSoilInfo
                    }
                ], undefined, true, true);
            }
            // set vadility becasue no onBlur in combobox component 
            if (value === '' || value === undefined) {
                this.formValidation.soilParameterForm.soilType = false;
                this.soilParameterFormClassName.soilType = ' usa-input--error'
            }
            else {
                this.formValidation.soilParameterForm.soilType = true;
                this.soilParameterFormClassName.soilType = ''
            }
            this.resetSoilParameterForm(value);
        }
        else if (name === 'void_ratio') {
            if (value < 0) { this.soilParameterForm.voidRatio = 0 }
            else { this.soilParameterForm.voidRatio = parseFloat(value) }
            this.resetCalculatedValues();
        }
        else if (name === 'plasticity_index') {
            if (value < 0) { this.soilParameterForm.plasticityIndex = 0 }
            else { this.soilParameterForm.plasticityIndex = parseFloat(value) }
            this.resetCalculatedValues();
        }
        else if (name === 'd75') {
            if (value < 0) { this.soilParameterForm.d75 = 0 }
            else { this.soilParameterForm.d75 = parseFloat(value) }
            this.resetCalculatedValues();
        }
    }

    @action.bound handleSoilStressInputChange(event) {
        const { name, value } = event.target;
        if (name === 'allowable_stress') {
            if (value < 0) { this.soilStressForm.allowableStress = 0 }
            else { this.soilStressForm.allowableStress = parseFloat(value) }
        }
        else if (name === 'soil_grain_roughness') {
            if (value < 0) { this.soilStressForm.soilGrainRoughness = 0 }
            else { this.soilStressForm.soilGrainRoughness = parseFloat(value) }
        }
    }

    @action.bound setFixedValue(name, fixed) {
        const event2 = {
            target: {
                name: name,
                value: fixed
            }
        }
        switch (name) {
            case 'void_ratio':
            case 'plasticity_index':
            case 'd75':
                this.handleSoilParameterInputChange(event2);
                break;
            case 'allowable_stress':
            case 'soil_grain_roughness':
                this.handleSoilStressInputChange(event2);
                break;
        }
    }

    @action.bound validateVoidRatio(minimum: number, maximum: number, fixed: any, errorCode: string) {
        this.formValidation.soilParameterForm.voidRatioInitialValueChange = true;
        if (fixed === "" || fixed < minimum || fixed > maximum) {
            MessageCenter.addNotificationFromCode(errorCode);
            this.formValidation.soilParameterForm.voidRatio = false;
            this.soilParameterFormClassName.voidRatio = ' usa-input--error';
        }
        else {
            this.formValidation.soilParameterForm.voidRatio = true;
            this.soilParameterFormClassName.voidRatio = '';
        }
    }

    @action.bound validatePlasticityIndex(minimum: number, maximum: number, fixed: any, errorCode: string) {
        this.formValidation.soilParameterForm.plasticityIndexInitialValueChange = true;
        if (fixed === "" || fixed < minimum || fixed > maximum) {
            MessageCenter.addNotificationFromCode(errorCode);
            this.formValidation.soilParameterForm.plasticityIndex = false;
            this.soilParameterFormClassName.plasticityIndex = ' usa-input--error';
        }
        else {
            this.formValidation.soilParameterForm.plasticityIndex = true;
            this.soilParameterFormClassName.plasticityIndex = '';
        }
    }

    @action.bound validateD75(minimum: number, maximum: number, fixed: any, errorCode: string) {
        this.formValidation.soilParameterForm.d75InitialValueChange = true;
        if (fixed === "" || fixed < minimum || fixed > maximum) {
            MessageCenter.addNotificationFromCode(errorCode);
            this.formValidation.soilParameterForm.d75 = false;
            this.soilParameterFormClassName.d75 = ' usa-input--error';
        }
        else {
            this.formValidation.soilParameterForm.d75 = true;
            this.soilParameterFormClassName.d75 = '';
        }
    }

    // handle vilidation of each parameter 
    @action.bound async validationFormSoilParameter(event) {
        const { name, value } = event.target;
        let fixed = value;
        if (value !== undefined && value !== '') {
            fixed = parseFloat(parseFloat(value).toFixed(2));
            this.setFixedValue(name, fixed);
        }
        switch (this.soilParameterForm.soilType) {
            case "":
                if (name === 'void_ratio') {
                    this.validateVoidRatio(0.17, 3.34, fixed, "WDT_26");
                } else if (name === 'plasticity_index') {
                    this.validatePlasticityIndex(0.01, 60, fixed, "WDT_27");
                } else if (name === 'd75') {
                    this.validateD75(0.01, 1, fixed, "WDT_04");
                }
                break
            case 'GW':
                if (name === 'void_ratio') {
                    this.validateVoidRatio(0.26, 0.46, fixed, "WDT_29");
                } else if (name === 'plasticity_index') {
                    this.validatePlasticityIndex(0.01, 60, fixed, "WDT_27");
                } else if (name === 'd75') {
                    this.validateD75(0.01, 1, fixed, "WDT_04");
                }
                break
            case 'GP':
                if (name === 'void_ratio') {
                    this.validateVoidRatio(0.26, 0.46, fixed, "WDT_30");
                } else if (name === 'plasticity_index') {
                    this.validatePlasticityIndex(0.01, 60, fixed, "WDT_27");
                } else if (name === 'd75') {
                    this.validateD75(0.01, 1, fixed, "WDT_04");
                }
                break
            case 'SW':
                if (name === 'void_ratio') {
                    this.validateVoidRatio(0.29, 0.74, fixed, "WDT_31");
                } else if (name === 'plasticity_index') {
                    this.validatePlasticityIndex(0.01, 60, fixed, "WDT_27");
                } else if (name === 'd75') {
                    this.validateD75(0.01, 1, fixed, "WDT_04");
                }
                break
            case 'SP':
                if (name === 'void_ratio') {
                    this.validateVoidRatio(0.30, 0.75, fixed, "WDT_32");
                } else if (name === 'plasticity_index') {
                    this.validatePlasticityIndex(0.01, 60, fixed, "WDT_27");
                } else if (name === 'd75') {
                    this.validateD75(0.01, 1, fixed, "WDT_04");
                }
                break
            case 'OL':
                if (name === 'void_ratio') {
                    this.formValidation.soilParameterForm.voidRatioInitialValueChange = true;
                    if (fixed === "" || fixed !== 1.9) {
                        MessageCenter.addNotificationFromCode("WDT_33");
                        this.formValidation.soilParameterForm.voidRatio = false;
                        this.soilParameterFormClassName.voidRatio = ' usa-input--error';
                    }
                    else {
                        this.formValidation.soilParameterForm.voidRatio = true;
                        this.soilParameterFormClassName.voidRatio = '';
                    }
                } else if (name === 'plasticity_index') {
                    this.validatePlasticityIndex(0.01, 60, fixed, "WDT_33");
                } else if (name === 'd75') {
                    this.validateD75(0.01, 1, fixed, "WDT_04");
                }
                break
            case 'OH':
                if (name === 'void_ratio') {
                    this.validateVoidRatio(1.06, 3.34, fixed, "WDT_28");
                } else if (name === 'plasticity_index') {
                    this.validatePlasticityIndex(0.01, 60, fixed, "WDT_28");
                } else if (name === 'd75') {
                    this.validateD75(0.01, 1, fixed, "WDT_04");
                }
                break
            //----------------------------------------------------------------
            case 'SC':
                if (name === 'void_ratio') {
                    this.validateVoidRatio(0.17, 0.59, fixed, "WDT_05");
                } else if (name === 'plasticity_index') {
                    this.validatePlasticityIndex(7, 60, fixed, "WDT_05");
                } else if (name === 'd75') {
                    this.validateD75(0.01, 1, fixed, "WDT_04");
                }
                break
            case 'GC':
                if (name === 'void_ratio') {
                    this.validateVoidRatio(0.21, 0.37, fixed, "WDT_06");
                } else if (name === 'plasticity_index') {
                    this.validatePlasticityIndex(7, 60, fixed, "WDT_06");
                } else if (name === 'd75') {
                    this.validateD75(0.01, 1, fixed, "WDT_04");
                }
                break
            case 'CH':
                if (name === 'void_ratio') {
                    this.validateVoidRatio(0.63, 1.45, fixed, "WDT_07");
                }
                else if (name === 'plasticity_index') {
                    this.validatePlasticityIndex(21.9, 60, fixed, "WDT_07");
                }
                else if (name === 'd75') {
                    this.validateD75(0.01, 1, fixed, "WDT_04");
                }
                break
            case 'CL':
                if (name === 'void_ratio') {
                    this.validateVoidRatio(0.41, 0.69, fixed, "WDT_08");
                } else if (name === 'plasticity_index') {
                    this.validatePlasticityIndex(7, 37.8, fixed, "WDT_08");
                }
                else if (name === 'd75') {
                    this.validateD75(0.01, 1, fixed, "WDT_04");
                }
                break
            case 'GM':
                if (name === 'void_ratio') {
                    this.validateVoidRatio(0.18, 0.28, fixed, "WDT_09");
                } else if (name === 'plasticity_index') {
                    this.validatePlasticityIndex(7, 60, fixed, "WDT_09");
                } else if (name === 'd75') {
                    this.validateD75(0.01, 1, fixed, "WDT_04");
                }
                break
            case 'SM':
                if (name === 'void_ratio') {
                    this.validateVoidRatio(0.33, 0.98, fixed, "WDT_10");
                }
                else if (name === 'plasticity_index') {
                    this.validatePlasticityIndex(7, 60, fixed, "WDT_10");
                }
                else if (name === 'd75') {
                    this.validateD75(0.01, 1, fixed, "WDT_04");
                }
                break
            case 'MH':
                if (name === 'void_ratio') {
                    this.validateVoidRatio(1.14, 2.1, fixed, "WDT_11");
                }
                else if (name === 'plasticity_index') {
                    this.validatePlasticityIndex(7, 60, fixed, "WDT_11");
                }
                else if (name === 'd75') {
                    this.validateD75(0.01, 1, fixed, "WDT_04");
                }
                break
            case 'ML':
                if (name === 'void_ratio') {
                    this.validateVoidRatio(0.26, 1.28, fixed, "WDT_12");
                }
                else if (name === 'plasticity_index') {
                    this.validatePlasticityIndex(7, 60, fixed, "WDT_12");
                }
                else if (name === 'd75') {
                    this.validateD75(0.01, 1, fixed, "WDT_04");
                }
                break
        }
    }

    @action.bound handleOnBlurSoilParameter(event) {
        this.validationFormSoilParameter(event);
        this.calculateAllowableStress();
        this.CalculateSoilGrainRoughness();
    }

    @action.bound handleOnBlurSoilStress(event) {
        const { name, value } = event.target;
        let fixed = value;
        if (value !== undefined && value !== '') {
            if (name === 'allowable_stress') {

                fixed = parseFloat(parseFloat(value).toFixed(3));
            }
            else if (name === 'soil_grain_roughness') {
                fixed = parseFloat(parseFloat(value).toFixed(4));
            }
            this.setFixedValue(name, fixed);
        }
        if (name === 'allowable_stress') {
            this.formValidation.soilStressForm.allowableStressInitialValueChange = true;
            if (value === "" || value < 0.02 || value > 0.1) {
                this.soilStressFormClassName.allowableStress = ' usa-input--error';
                this.formValidation.soilStressForm.allowableStress = false;
                MessageCenter.addNotificationFromCode('WDT_01');
            }
            else {
                this.soilStressFormClassName.allowableStress = '';
                this.formValidation.soilStressForm.allowableStress = true;
            }

        }
        else if (name === 'soil_grain_roughness') {
            this.formValidation.soilStressForm.soilGrainRoughnessInitialValueChange = true;
            if (value === "" || value < 0.01 || value > 0.03) {
                this.soilStressFormClassName.soilGrainRoughness = ' usa-input--error';
                this.formValidation.soilStressForm.soilGrainRoughness = false;
                MessageCenter.addNotificationFromCode('WDT_03');
            }
            else {
                this.soilStressFormClassName.soilGrainRoughness = '';
                this.formValidation.soilStressForm.soilGrainRoughness = true;
            }

        }
    }

    @action.bound async resetRedHighlightingSoilParameterForm() {
        this.soilParameterFormClassName.d75 = "";
        this.soilParameterFormClassName.plasticityIndex = "";
        this.soilParameterFormClassName.voidRatio = "";
    }
    // reset all fields and calculated fields except soil type when soil type is changed 
    @action.bound async resetSoilParameterForm(soilType) {
        this.resetRedHighlightingSoilParameterForm();
        if (soilType === "") {
            this.soilParameterForm.allowableStress = "";
            this.soilParameterForm.d75 = "";
            this.soilParameterForm.plasticityIndex = "";
            this.soilParameterForm.soilGrainRoughness = "";
            this.soilParameterForm.voidRatio = "";
            this.formValidation.soilParameterForm.d75 = false;
            this.formValidation.soilParameterForm.allowableStress = false;
            this.formValidation.soilParameterForm.d75InitialValueChange = false;
            this.formValidation.soilParameterForm.plasticityIndex = false;
            this.formValidation.soilParameterForm.plasticityIndexInitialValueChange = false;
            this.formValidation.soilParameterForm.soilGrainRoughness = false;
            this.formValidation.soilParameterForm.voidRatio = false;
            this.formValidation.soilParameterForm.voidRatioInitialValueChange = false;
            this.formaValidationResult.validationResultSoilParameterForm = false;
        }
        else if (soilType === "GW" || soilType === "GP" || soilType === "SW" || soilType === "SP") {
            this.soilParameterForm.allowableStress = "";
            this.soilParameterForm.d75 = "";
            this.soilParameterForm.plasticityIndex = 0.01;
            this.soilParameterForm.soilGrainRoughness = "";
            this.soilParameterForm.voidRatio = "";
            this.formValidation.soilParameterForm.d75 = false;
            this.formValidation.soilParameterForm.allowableStress = false;
            this.formValidation.soilParameterForm.d75InitialValueChange = false;
            this.formValidation.soilParameterForm.plasticityIndex = true;
            this.formValidation.soilParameterForm.plasticityIndexInitialValueChange = false;
            this.formValidation.soilParameterForm.soilGrainRoughness = false;
            this.formValidation.soilParameterForm.voidRatio = false;
            this.formValidation.soilParameterForm.voidRatioInitialValueChange = false;
            this.formaValidationResult.validationResultSoilParameterForm = false;
        }
        else if (soilType === "OH" || soilType === "OL" || soilType === "SC" || soilType === "GC" ||
            soilType === "GW" || soilType === "CH" || soilType === "CL" || soilType === "GM" || soilType === "SM"
            || soilType === "MH" || soilType === "ML") {
            this.soilParameterForm.allowableStress = "";
            this.soilParameterForm.d75 = 0.05;
            this.soilParameterForm.plasticityIndex = "";
            this.soilParameterForm.soilGrainRoughness = "";
            this.soilParameterForm.voidRatio = "";
            this.formValidation.soilParameterForm.d75 = true;
            this.formValidation.soilParameterForm.allowableStress = false;
            this.formValidation.soilParameterForm.d75InitialValueChange = false;
            this.formValidation.soilParameterForm.plasticityIndex = true;
            this.formValidation.soilParameterForm.plasticityIndexInitialValueChange = false;
            this.formValidation.soilParameterForm.soilGrainRoughness = false;
            this.formValidation.soilParameterForm.voidRatio = false;
            this.formValidation.soilParameterForm.voidRatioInitialValueChange = false;
            this.formaValidationResult.validationResultSoilParameterForm = false;
            if (soilType === 'OL') {
                this.soilParameterForm.voidRatio = 1.90;
                this.formValidation.soilParameterForm.voidRatio = true;
            }
        }

    }

    // On Change of other Soil Parameters, clear calculated values (Allowable Stress and Soil Grain Roughness)
    @action.bound async resetCalculatedValues() {
        this.soilParameterForm.allowableStress = "";
        this.soilParameterForm.soilGrainRoughness = "";
        this.formValidation.soilParameterForm.allowableStress = false;
        this.formValidation.soilParameterForm.soilGrainRoughness = false;
    }

    @action.bound handleFormSelect(event) {
        const { name, value } = event.target;
        this.soilFormSelect.type = name;
    }

    //api call to receive all data
    @action.bound async getParameters() {
        try {
            const response: any = await PRJAPI.ParameterController.getDefaultParameterByIdUsingGET(1);

            if (response.data) {
                const object = JSON.parse(response.data.json);
                // soil type dropdown data process
                const soilTypeObject = object['soilTypes'];
                this.soilTypeMenu = soilTypeObject.map((e) => ({ label: e['lable'], description: e['description'] }));
                this.soilTypeOptions = this.soilTypeMenu.map((e) => ({ name: e['label'] + ' (' + e['description'] + ')', value: e['label'] }));
                // erodibility dropdown data process
                const erodibilityObject = object['stressFactors'];
                this.erodibilityMenu = erodibilityObject.map((e) => ({ label: e['lable'], soilType: e['explanation'], allowableStress: e['stressFactorValue'], roughness: e['roughness'] }));
                this.erodibilityOptions = this.erodibilityMenu.map((e) => ({ name: e['label'].split('_').reduce((prev, current) => (prev + ' ' + current)) + ', ' + 'Allowable Stress (' + e['allowableStress'].toString() + ' lb/sq ft), Soil Type (' + e['soilType'] + ')', value: e['label'] }));
                if (this.erodibilityForm.erodibility !== '') {
                    this.handleErodibilityInputChange(({
                        target: {
                            name: 'erodibility_dropdown',
                            value: this.erodibilityForm.erodibility
                        }
                    }))
                }
                if (this.erodibilityForm.allowableStress !== 0) {
                    const erodibility = (this.erodibilityMenu as any[]).find((obj) => obj.allowableStress === this.erodibilityForm.allowableStress);
                    if (erodibility === undefined) {
                        this.soilFormSelect.type = 'soil_stress';
                        this.handleOnBlurSoilStress({
                            target: {
                                name: 'allowable_stress',
                                value: this.soilStressForm.allowableStress
                            }
                        })
                    }
                    else {
                        this.handleErodibilityInputChange(({
                            target: {
                                name: 'erodibility_dropdown',
                                value: erodibility.label
                            }
                        }))
                    }

                }
            }
        } catch (error) {
            console.error(' error: get waterway sizing calculator getDefaultParameterByIdUsingGET error', error);
        }
    }

    @action.bound async calculateAllowableStress() {
        if (this.formValidation.soilParameterForm.soilType
            && this.formValidation.soilParameterForm.d75
            && this.formValidation.soilParameterForm.voidRatio
            && this.formValidation.soilParameterForm.plasticityIndex
            && this.soilParameterForm.soilType !== ""
            && this.soilParameterForm.d75 !== ""
            && this.soilParameterForm.plasticityIndex !== ""
            && this.soilParameterForm.voidRatio !== ""
            && this.soilParameterForm.d75 !== "RESET"
            && this.soilParameterForm.plasticityIndex !== "RESET"
            && this.soilParameterForm.voidRatio !== "RESET"
        ) {
            const postJSON = {
                d75: this.soilParameterForm.d75,
                soilType: this.soilParameterForm.soilType,
                plasticity: this.soilParameterForm.plasticityIndex,
                voidRatio: this.soilParameterForm.voidRatio
            };
            try {
                const response: any = await SETAPI.WaterwaySizingController.calculateSoilStressUsingPOST(postJSON);
                if (response && response.data && response.data.allowableStress) {
                    this.soilParameterForm.allowableStress = toDecimalPrecision(response.data.allowableStress, 3);
                    if (this.soilParameterForm.allowableStress < 0.020 || this.soilParameterForm.allowableStress > 0.100) {
                        MessageCenter.addNotificationFromCode('WDT_01')
                        this.soilParameterFormClassName.allowableStress = 'usa-input--error';
                        this.formValidation.soilParameterForm.allowableStress = false;
                    } else {
                        this.soilParameterFormClassName.allowableStress = 'calculated-result-box';
                        this.formValidation.soilParameterForm.allowableStress = true;
                    }
                }
            }
            catch (error) {
                this.formValidation.soilParameterForm.allowableStress = false;
                console.error(error);
            }
        }
        else {
            return
        }
    }
    @action.bound async CalculateSoilGrainRoughness() {
        if (this.formValidation.soilParameterForm.soilType
            && this.formValidation.soilParameterForm.d75
            && this.formValidation.soilParameterForm.voidRatio
            && this.formValidation.soilParameterForm.plasticityIndex
            && this.soilParameterForm.soilType !== ""
            && this.soilParameterForm.d75 !== ""
            && this.soilParameterForm.plasticityIndex !== ""
            && this.soilParameterForm.voidRatio !== ""
            && this.soilParameterForm.d75 !== "RESET"
            && this.soilParameterForm.plasticityIndex !== "RESET"
            && this.soilParameterForm.voidRatio !== "RESET") {
            const postJSON = {
                d75: this.soilParameterForm.d75,
                soilType: this.soilParameterForm.soilType
            };
            try {
                const response: any = await SETAPI.WaterwaySizingController.calculateSoilGrainRoughnessUsingPOST(postJSON);
                if (response && response.data && response.data.soilGrainRoughness) {
                    this.soilParameterForm.soilGrainRoughness = toDecimalPrecision(response.data.soilGrainRoughness, 4);
                    this.formValidation.soilParameterForm.soilGrainRoughness = true;
                }
            }
            catch (error) {
                this.formValidation.soilParameterForm.soilGrainRoughness = false;
                console.error(error);
            }
        }
        else {
            return
        }
    }

    get exportSoilStrengthDto(): SoilParametersDTO {
        const soilStrengthParams: SoilParametersDTO = {};
        if (this.soilStrengthSummaryValidation) {
            soilStrengthParams.inputModeStr = undefined;
            switch (this.soilFormSelect.type) {
                case 'erodibility':
                    soilStrengthParams.inputModeStr = "Erodibility Class";
                    soilStrengthParams.allowableStress = this.erodibilityForm.allowableStress;
                    soilStrengthParams.soilGrainRoughness = this.erodibilityForm.soilGrainRoughness;
                    break
                case 'soil_parameter':
                    soilStrengthParams.inputModeStr = "Soil Parameter";
                    soilStrengthParams.allowableStress = this.soilParameterForm.allowableStress ? this.soilParameterForm.allowableStress : 0;
                    soilStrengthParams.soilGrainRoughness = this.soilParameterForm.soilGrainRoughness ? this.soilParameterForm.soilGrainRoughness : 0;
                    break
                case 'soil_stress':
                    soilStrengthParams.inputModeStr = "Soil Stress"
                    soilStrengthParams.allowableStress = (this.soilStressForm.allowableStress && this.soilStressForm.allowableStress !== "RESET") ? this.soilStressForm.allowableStress : 0;
                    soilStrengthParams.soilGrainRoughness = (this.soilStressForm.soilGrainRoughness && this.soilStressForm.soilGrainRoughness !== "RESET") ? this.soilStressForm.soilGrainRoughness : 0;
                    break
            }
            soilStrengthParams.stressFactorStr = this.erodibilityForm.erodibility;
            soilStrengthParams.soilTypeStr = this.soilParameterForm.soilType;
            soilStrengthParams.voidRatio = (this.soilParameterForm.voidRatio && this.soilParameterForm.voidRatio !== "RESET") ? this.soilParameterForm.voidRatio : 0;
            soilStrengthParams.plasticityIndex = (this.soilParameterForm.plasticityIndex && this.soilParameterForm.plasticityIndex !== "RESET") ? this.soilParameterForm.plasticityIndex : 0;
            soilStrengthParams.d75 = (this.soilParameterForm.d75 && this.soilParameterForm.d75 !== "RESET") ? this.soilParameterForm.d75 : 0;
        }
        return soilStrengthParams;
    }
}